import React, { useState, useEffect, useRef } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Line,
} from 'recharts';
import CustomTooltip from './CustomTooltip';
import styled from 'styled-components';
import { getStocksPriceChart } from '@/src/services/stockDetails';
import { IStockChart } from '../types';
import ChartDetailsShimmer from '../components/ChartDetailsShimmer';
import { useSelector } from 'react-redux';
import { selectStockDetails } from '@/src/store/reducers/stocks/stockDetailsSlice';
import { NoData } from './NoData';

const MainContainer = styled.div`
  width: 100%;
  height: 30vh;
  @media screen and (max-width: 900px) {
    width: 100vw;
    position: relative;
    left: -16px;
  }
`;

interface StocksPriceChartProps {
  periodLevel: string;
}

export const StocksPriceChart: React.FC<StocksPriceChartProps> = ({
  periodLevel,
}) => {
  const {
    data: { cocode, exchange },
  } = useSelector(selectStockDetails);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<IStockChart[]>([]);
  const [error, setError] = useState<string | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cocode && exchange && periodLevel) {
      setLoading(true);
      setError(null);
      getStocksPriceChart(cocode, exchange.toString(), periodLevel)
        .then((response) => {
          const dataWithPercentage = response.data.map(
            (item: IStockChart, index: number, array: IStockChart[]) => {
              const prevClose = index > 0 ? array[index - 1].close : null;
              const percentage = prevClose
                ? ((item.close - prevClose) / prevClose) * 100
                : 0;
              return { ...item, percentage };
            },
          );
          setChartData(dataWithPercentage);
        })
        .catch(() => {
          setError('Failed to load chart data');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [exchange, periodLevel, cocode]);

  if (isLoading) {
    return <ChartDetailsShimmer />;
  }

  if (error) {
    return <NoData />;
  }

  const firstClose = chartData[0]?.close ?? 0;
  const lastClose = chartData[chartData.length - 1]?.close ?? 0;
  const isPositive = lastClose > firstClose;
  const areaColor = isPositive ? '#0BC1A2' : '#FF6347';

  if (chartData.length !== 0 && Object.keys(chartData[0]).length <= 1) {
    return <NoData />;
  }

  return (
    <MainContainer ref={chartRef}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="9.88%" stopColor={areaColor} stopOpacity={1} />
              <stop
                offset="90.12%"
                stopColor={`${areaColor}33`}
                stopOpacity={1}
              />
              <stop
                offset="100%"
                stopColor={`${areaColor}00`}
                stopOpacity={1}
              />
            </linearGradient>
          </defs>
          <XAxis dataKey="date" axisLine={false} hide={true} />
          <YAxis domain={['auto', 'auto']} axisLine={false} hide={true} />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="high"
            stroke="#FF6347"
            strokeWidth="2"
            dot={{ r: 5, fill: '#FF6347' }}
          />
          <Area
            type="monotone"
            dataKey="close"
            stroke={areaColor}
            strokeWidth="2.5px"
            fill="url(#colorUv)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </MainContainer>
  );
};
